import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import BusinessLayout from '../../components/layout/BusinessLayout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { 
  BuildingOfficeIcon, 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import { buildApiUrl } from '../../config/api';

interface Venue {
  id: string;
  name: string;
  address: string;
  description?: string;
  city: string;
  postcode: string;
  phone?: string;
  email?: string;
  capacity: number;
  facilities?: string[];
  isActive: boolean;
  tfcEnabled?: boolean;
  tfcHoldPeriod?: number;
  tfcInstructions?: string;
  tfcDefaultToCredit?: boolean;
  createdAt: string;
  updatedAt: string;
}

const VenuesPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<Venue>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchVenues();
  }, []);

  const fetchVenues = async () => {
    try {
      setLoading(true);
      const token = authService.getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout

      const response = await fetch(buildApiUrl('/business/venues'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to fetch venues');
      }

      const data = await response.json();
      if (data.success) {
        // Transform API data to match our interface
        const transformedVenues: Venue[] = (data.data.venues || []).map((venue: any) => ({
          id: venue.id,
          name: venue.name,
          address: venue.address || '',
          city: venue.city || '',
          postcode: venue.postcode || '',
          phone: venue.phone,
          email: venue.email,
          capacity: venue.capacity || 0,
          facilities: venue.facilities || [],
          isActive: venue.isActive !== undefined ? venue.isActive : true,
          createdAt: venue.createdAt,
          updatedAt: venue.updatedAt
        }));
        setVenues(transformedVenues);
      } else {
        throw new Error(data.message || 'Failed to fetch venues');
      }
    } catch (error) {
      console.error('Error fetching venues:', error);
      if (error instanceof Error && error.name === 'AbortError') {
        toast.error('Venues loading timeout - please refresh');
      } else {
        toast.error('Failed to load venues');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredVenues = venues.filter(venue => {
    const matchesSearch = venue.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         venue.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         venue.city.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && venue.isActive) ||
                         (statusFilter === 'inactive' && !venue.isActive);
    
    return matchesSearch && matchesStatus;
  });

  const handleDeleteVenue = async (venueId: string) => {
    if (window.confirm('Are you sure you want to delete this venue?')) {
      try {
        const token = authService.getToken();
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await fetch(buildApiUrl(`/business/venues/${venueId}`), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to delete venue');
        }

        const data = await response.json();
        if (data.success) {
          setVenues(prev => prev.filter(v => v.id !== venueId));
          toast.success('Venue deleted successfully');
        } else {
          throw new Error(data.message || 'Failed to delete venue');
        }
      } catch (error) {
        console.error('Error deleting venue:', error);
        toast.error('Failed to delete venue');
      }
    }
  };

  const handleToggleStatus = async (venueId: string) => {
    try {
      const token = authService.getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(buildApiUrl(`/business/venues/${venueId}/toggle`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to toggle venue status');
      }

      const data = await response.json();
      if (data.success) {
        const updatedVenue = data.data;
        setVenues(prev => prev.map(v => 
          v.id === venueId ? { 
            ...v, 
            isActive: updatedVenue.isActive,
            updatedAt: updatedVenue.updatedAt
          } : v
        ));
        toast.success('Venue status updated');
      } else {
        throw new Error(data.message || 'Failed to update venue');
      }
    } catch (error) {
      console.error('Error updating venue:', error);
      toast.error('Failed to update venue');
    }
  };

  const handleEditVenue = async (venue: Venue) => {
    try {
      // Fetch full venue details including TFC settings
      const token = authService.getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(buildApiUrl(`/business/venues/${venue.id}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const fullVenue = data.data;
        
        setEditingVenue(fullVenue);
        setEditFormData({
          name: fullVenue.name || '',
          address: fullVenue.address || '',
          description: fullVenue.description || '',
          city: fullVenue.city || '',
          postcode: fullVenue.postcode || '',
          phone: fullVenue.phone || '',
          email: fullVenue.email || '',
          capacity: fullVenue.capacity || 0,
          isActive: fullVenue.isActive !== undefined ? fullVenue.isActive : true,
          tfcEnabled: fullVenue.tfcEnabled !== undefined ? fullVenue.tfcEnabled : true,
          tfcHoldPeriod: fullVenue.tfcHoldPeriod || 5,
          tfcInstructions: fullVenue.tfcInstructions || '',
          tfcDefaultToCredit: fullVenue.tfcDefaultToCredit !== undefined ? fullVenue.tfcDefaultToCredit : true
        });
        setShowEditModal(true);
      } else {
        // Fallback to venue from list if fetch fails
        setEditingVenue(venue);
        setEditFormData({
          name: venue.name,
          address: venue.address,
          description: venue.description || '',
          city: venue.city,
          postcode: venue.postcode,
          phone: venue.phone || '',
          email: venue.email || '',
          capacity: venue.capacity,
          isActive: venue.isActive,
          tfcEnabled: venue.tfcEnabled !== undefined ? venue.tfcEnabled : true,
          tfcHoldPeriod: venue.tfcHoldPeriod || 5,
          tfcInstructions: venue.tfcInstructions || '',
          tfcDefaultToCredit: venue.tfcDefaultToCredit !== undefined ? venue.tfcDefaultToCredit : true
        });
        setShowEditModal(true);
      }
    } catch (error) {
      console.error('Error fetching venue details:', error);
      // Fallback to venue from list if fetch fails
      setEditingVenue(venue);
      setEditFormData({
        name: venue.name,
        address: venue.address,
        description: venue.description || '',
        city: venue.city,
        postcode: venue.postcode,
        phone: venue.phone || '',
        email: venue.email || '',
        capacity: venue.capacity,
        isActive: venue.isActive,
        tfcEnabled: venue.tfcEnabled !== undefined ? venue.tfcEnabled : true,
        tfcHoldPeriod: venue.tfcHoldPeriod || 5,
        tfcInstructions: venue.tfcInstructions || '',
        tfcDefaultToCredit: venue.tfcDefaultToCredit !== undefined ? venue.tfcDefaultToCredit : true
      });
      setShowEditModal(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingVenue) return;

    try {
      setSaving(true);
      const token = authService.getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(buildApiUrl(`/business/venues/${editingVenue.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editFormData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update venue');
      }

      const data = await response.json();
      if (data.success) {
        const updatedVenue = data.data;
        setVenues(prev => prev.map(v => 
          v.id === editingVenue.id ? { 
            ...updatedVenue,
            phone: updatedVenue.phone || v.phone,
            email: updatedVenue.email || v.email,
            facilities: v.facilities // Preserve facilities as it's not in the update response
          } : v
        ));
        toast.success('Venue updated successfully');
        setShowEditModal(false);
        setEditingVenue(null);
        fetchVenues(); // Refresh the list to get all updated data
      } else {
        throw new Error(data.message || 'Failed to update venue');
      }
    } catch (error) {
      console.error('Error updating venue:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update venue');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingVenue(null);
    setEditFormData({});
  };

  if (loading) {
    return (
      <BusinessLayout user={user}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-64 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white p-6 rounded-lg shadow">
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/2 mb-4"></div>
                  <div className="h-3 bg-gray-300 rounded w-full mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </BusinessLayout>
    );
  }

  return (
    <BusinessLayout user={user}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Venues</h1>
            <p className="text-gray-600 mt-1">Manage your business venues and locations</p>
          </div>
          <Button 
            className="flex items-center gap-2"
            onClick={() => navigate('/business/venue-setup')}
          >
            <PlusIcon className="h-5 w-5" />
            Add Venue
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search venues..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00806a] focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00806a] focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                }}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </Card>

        {/* Venues Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVenues.map((venue) => (
            <Card key={venue.id} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <BuildingOfficeIcon className="h-8 w-8 text-[#00806a]" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{venue.name}</h3>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        venue.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {venue.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <span className="text-xs text-gray-500">Capacity: {venue.capacity}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-start gap-2">
                  <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="text-sm text-gray-600">
                    <p>{venue.address}</p>
                    <p>{venue.city}, {venue.postcode}</p>
                  </div>
                </div>
                
                {venue.phone && (
                  <div className="flex items-center gap-2">
                    <PhoneIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{venue.phone}</span>
                  </div>
                )}
                
                {venue.email && (
                  <div className="flex items-center gap-2">
                    <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{venue.email}</span>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Facilities:</p>
                <div className="flex flex-wrap gap-1">
                  {(venue.facilities || []).map((facility, index) => (
                    <span 
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {facility}
                    </span>
                  ))}
                  {(!venue.facilities || venue.facilities.length === 0) && (
                    <span className="text-sm text-gray-500 italic">No facilities listed</span>
                  )}
                </div>
              </div>

              <div className="pt-4 border-t">
                <div className="mb-2 p-2 bg-gray-50 rounded text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Venue ID:</span>
                    <div className="flex items-center gap-1">
                      <code className="text-gray-800 font-mono">{venue.id}</code>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(venue.id);
                          toast.success('Venue ID copied!');
                        }}
                        className="p-0.5 text-gray-500 hover:text-blue-600"
                        title="Copy Venue ID"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Updated {new Date(venue.updatedAt).toLocaleDateString()}
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => handleEditVenue(venue)}
                      className="p-1 text-gray-400 hover:text-blue-600"
                      title="Edit Venue"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleDeleteVenue(venue.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {filteredVenues.length === 0 && (
          <Card className="p-12 text-center">
            <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No venues found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search criteria'
                : 'Get started by adding your first venue'
              }
            </p>
            <Button>
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Venue
            </Button>
          </Card>
        )}

        {/* Edit Venue Modal */}
        <Modal
          isOpen={showEditModal}
          onClose={handleCancelEdit}
          title="Edit Venue"
          size="lg"
        >
          {editingVenue && (
            <div className="space-y-4 max-h-[80vh] overflow-y-auto">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Venue Name *
                  </label>
                  <Input
                    type="text"
                    value={editFormData.name || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    placeholder="Enter venue name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <Textarea
                    value={editFormData.description || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    placeholder="Enter venue description"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address *
                  </label>
                  <Input
                    type="text"
                    value={editFormData.address || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                    placeholder="Enter address"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <Input
                      type="text"
                      value={editFormData.city || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                      placeholder="Enter city"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Postcode
                    </label>
                    <Input
                      type="text"
                      value={editFormData.postcode || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, postcode: e.target.value })}
                      placeholder="Enter postcode"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <Input
                      type="tel"
                      value={editFormData.phone || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <Input
                      type="email"
                      value={editFormData.email || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                      placeholder="Enter email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Capacity
                  </label>
                  <Input
                    type="number"
                    value={editFormData.capacity || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, capacity: parseInt(e.target.value) || 0 })}
                    placeholder="Enter capacity"
                  />
                </div>

                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={editFormData.isActive !== undefined ? editFormData.isActive : true}
                      onChange={(e) => setEditFormData({ ...editFormData, isActive: e.target.checked })}
                      className="rounded border-gray-300 text-[#00806a] focus:ring-[#00806a]"
                    />
                    <span className="text-sm font-medium text-gray-700">Active Status</span>
                  </label>
                  <p className="text-xs text-gray-500 ml-6 mt-1">Inactive venues won't appear in booking options</p>
                </div>
              </div>

              {/* TFC Settings */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">TFC (Transfer to Credit) Settings</h3>
                
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={editFormData.tfcEnabled !== undefined ? editFormData.tfcEnabled : true}
                      onChange={(e) => setEditFormData({ ...editFormData, tfcEnabled: e.target.checked })}
                      className="rounded border-gray-300 text-[#00806a] focus:ring-[#00806a]"
                    />
                    <span className="text-sm font-medium text-gray-700">Enable TFC</span>
                  </label>
                  <p className="text-xs text-gray-500 ml-6 mt-1">Allow transfers to credit for this venue</p>
                </div>

                {editFormData.tfcEnabled && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        TFC Hold Period (Days)
                      </label>
                      <Input
                        type="number"
                        value={editFormData.tfcHoldPeriod || 5}
                        onChange={(e) => setEditFormData({ ...editFormData, tfcHoldPeriod: parseInt(e.target.value) || 5 })}
                        placeholder="Enter hold period in days"
                        min={1}
                      />
                      <p className="text-xs text-gray-500 mt-1">Number of days before TFC credit is available</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        TFC Instructions
                      </label>
                      <Textarea
                        value={editFormData.tfcInstructions || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, tfcInstructions: e.target.value })}
                        placeholder="Enter TFC instructions for customers"
                        rows={3}
                      />
                      <p className="text-xs text-gray-500 mt-1">Instructions displayed to customers about TFC</p>
                    </div>

                    <div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={editFormData.tfcDefaultToCredit !== undefined ? editFormData.tfcDefaultToCredit : true}
                          onChange={(e) => setEditFormData({ ...editFormData, tfcDefaultToCredit: e.target.checked })}
                          className="rounded border-gray-300 text-[#00806a] focus:ring-[#00806a]"
                        />
                        <span className="text-sm font-medium text-gray-700">Default to Credit</span>
                      </label>
                      <p className="text-xs text-gray-500 ml-6 mt-1">Automatically apply credit for cancellations</p>
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={saving || !editFormData.name || !editFormData.address}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </BusinessLayout>
  );
};

export default VenuesPage;
